/**
 * Glance4x1Widget.ts
 *
 * Hourly Horizon (4x1) glance strip widget.
 * Displays immediate next 4 forecast intervals with conditions, temperatures, and winds.
 */

import store from '../../../../state';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export class Glance4x1Widget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Hourly Horizon (4×1)';
    const data = ctx.data;
    const isImperial = store.getState().units === 'imperial';
    const tempUnit = isImperial ? '°' : '°C';
    const speedUnit = isImperial ? 'mph' : 'km/h';

    const labels = data.timeLabels || [];
    const temps = data.tempsForecast || [];
    const clouds = data.cloudCover || [];
    const precips = data.precipLikelihood || [];
    const winds = data.windSpeed || [];

    const tilesHtml: string[] = [];
    const count = Math.min(4, Math.max(1, labels.length));

    for (let i = 0; i < count; i++) {
      const rawLbl = labels[i] || `+${i + 1}h`;
      const shortLbl = rawLbl.includes(' ') ? rawLbl.split(' ').slice(1).join(' ') : rawLbl;

      const tF = temps[i] !== undefined ? temps[i] : 65;
      const dispT = isImperial ? Math.round(tF) : Math.round((tF - 32) * 5 / 9);

      const cl = clouds[i] !== undefined ? clouds[i] : 30;
      const pr = precips[i] !== undefined ? precips[i] : 0;
      const wMph = winds[i] !== undefined ? winds[i] : 8;
      const dispW = isImperial ? Math.round(wMph) : Math.round(wMph * 1.60934);

      let icon = '☀️';
      if (pr > 40) icon = '🌧️';
      else if (cl > 70) icon = '☁️';
      else if (cl > 30) icon = '⛅';

      tilesHtml.push(`
        <div style="background: rgba(15, 23, 42, 0.6); padding: 8px 6px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.06); text-align: center; display: flex; flex-direction: column; gap: 3px;">
          <div style="font-size: 0.68rem; color: #94a3b8; font-weight: 600;">${shortLbl}</div>
          <div style="font-size: 1.3rem; margin: 2px 0;">${icon}</div>
          <div style="font-size: 1.15rem; font-weight: 800; color: #fff; font-family: monospace;">${dispT}${tempUnit}</div>
          <div style="font-size: 0.65rem; color: ${pr > 20 ? '#38bdf8' : '#94a3b8'}; font-weight: 600;">${pr > 0 ? `${Math.round(pr)}% rain` : `${dispW} ${speedUnit}`}</div>
        </div>
      `);
    }

    return `
      <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">⏱️ ${title}</span>
          <span style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.3); font-size: 0.65rem; font-weight: 700; padding: 2px 7px; border-radius: 4px;">4-Hour Glance</span>
        </div>
        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px;">
          ${tilesHtml.join('')}
        </div>
      </div>
    `;
  }

  public init(_widget: ForecastWidgetConfig, _ctx: WidgetRenderContext): void {}
}
